/**
 * LawInterpretationPanel — 관세청 법령해석(국가법령정보센터, 서버가 받아 둔 것)을 검색어·세번으로 찾아 보인다.
 *   분류사례 탭 아래에 붙는다. 세번은 분류사례에서 가장 많이 나온 6자리를 쓴다(없으면 낱말만).
 *   제목을 누르면 질의요지·회답·이유 전문을 편다. 판정이 아니라 근거 자료다.
 */
import React, { useEffect, useMemo, useState } from "react";

function authHeaders() {
  const t = localStorage.getItem("token") || sessionStorage.getItem("token") || "";
  return t ? { Authorization: `Bearer ${t}` } : {};
}

/** 분류사례 목록에서 가장 많이 나온 6자리 세번. */
export function topHs6(cases) {
  const n = {};
  for (const c of cases || []) {
    const h = String((c && (c.hsk || c.hs6)) || "").replace(/\D/g, "").slice(0, 6);
    if (h.length === 6) n[h] = (n[h] || 0) + 1;
  }
  return Object.entries(n).sort((a, b) => b[1] - a[1])[0]?.[0] || "";
}

export default function LawInterpretationPanel({ q, cases }) {
  const hs = useMemo(() => topHs6(cases), [cases]);
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState({});
  useEffect(() => {
    if (!q && !hs) return undefined;
    let alive = true;
    setItems(null);
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (hs) p.set("hs", hs);
    fetch(`/api/onto/law-interpretations?${p}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => alive && setItems((d && d.items) || []))
      .catch(() => alive && setItems([]));
    return () => {
      alive = false;
    };
  }, [q, hs]);

  const toggle = (id) => {
    if (open[id]) return setOpen((o) => ({ ...o, [id]: null }));
    setOpen((o) => ({ ...o, [id]: "loading" }));
    fetch(`/api/onto/law-interpretations/${id}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setOpen((o) => ({ ...o, [id]: d || "error" })))
      .catch(() => setOpen((o) => ({ ...o, [id]: "error" })));
  };

  if (items === null) return <div style={{ fontSize: 12.5, color: "var(--audit-text-muted)", padding: "8px 0" }}>관세청 법령해석 찾는 중…</div>;
  if (!items.length) return <div data-testid="law-interp-empty" style={{ fontSize: 12.5, color: "var(--audit-text-muted)", padding: "8px 0" }}>관련 관세청 법령해석 없음</div>;
  return (
    <div data-testid="law-interp" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {items.map((it) => {
        const d = open[it.serial_no];
        return (
          <div key={it.serial_no} style={{ border: "1px solid var(--audit-border, #e5e7eb)", borderRadius: 8, padding: "8px 12px" }}>
            <button
              type="button"
              onClick={() => toggle(it.serial_no)}
              aria-expanded={!!d}
              style={{ all: "unset", cursor: "pointer", fontWeight: 600, fontSize: 13.5, color: "var(--audit-text, #111)" }}
            >
              {d ? "▾" : "▸"} {it.title}
            </button>
            <div style={{ fontSize: 12, color: "var(--audit-text-muted)", marginTop: 2 }}>
              {[it.agency, it.date, it.field, it.matched_hs && `세번 ${it.matched_hs} 언급`].filter(Boolean).join(" · ")}
            </div>
            {!d && it.answer_gist && <div style={{ fontSize: 12.5, marginTop: 4, lineHeight: 1.6 }}>{it.answer_gist}</div>}
            {d === "loading" && <div style={{ fontSize: 12.5, marginTop: 6 }}>불러오는 중…</div>}
            {d === "error" && <div style={{ fontSize: 12.5, marginTop: 6 }}>본문을 불러오지 못했습니다.</div>}
            {d && typeof d === "object" && (
              <div style={{ fontSize: 13, marginTop: 6, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                {[["질의요지", d.question], ["회답", d.answer], ["이유", d.reason], ["관련 법령", d.related_law]]
                  .filter(([, v]) => v)
                  .map(([k, v]) => (
                    <div key={k} style={{ marginBottom: 8 }}>
                      <b>{k}</b>
                      <div>{v}</div>
                    </div>
                  ))}
                <div style={{ fontSize: 11.5, color: "var(--audit-text-muted)" }}>출처: {d.source} · 일련번호 {d.serial_no}</div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
